import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Wrench, Loader2, Save, Plus, Trash2, Star, StarOff } from "lucide-react";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-zinc-50 focus:border-[#00E575] outline-none text-sm";
const labelCls = "text-[12px] font-semibold uppercase tracking-wider text-zinc-400";

const blankInstaller = () => ({
  id: null,
  name: "",
  contact_person: "",
  street: "",
  house_number: "",
  zip: "",
  city: "",
  email: "",
  phone: "",
  notes: "",
  is_default: false,
});

export default function AdminInstallers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editing, setEditing] = useState(null); // current installer being edited

  const load = () => {
    setLoading(true);
    api.get("/installers").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startNew = () => setEditing(blankInstaller());
  const startEdit = (inst) => setEditing({ ...inst });

  const updField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (!editing?.name?.trim()) return toast.error("Name ist Pflichtfeld");
    setSavingId(editing.id || "new");
    try {
      const payload = {
        name: editing.name.trim(),
        contact_person: editing.contact_person || "",
        street: editing.street || "",
        house_number: editing.house_number || "",
        zip: editing.zip || "",
        city: editing.city || "",
        email: editing.email || "",
        phone: editing.phone || "",
        notes: editing.notes || "",
        is_default: !!editing.is_default,
      };
      if (editing.id) {
        await api.patch(`/installers/${editing.id}`, payload);
        toast.success("Installation aktualisiert");
      } else {
        await api.post("/installers", payload);
        toast.success("Installation angelegt");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (inst) => {
    if (!window.confirm(`„${inst.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/installers/${inst.id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const setDefault = async (inst) => {
    try {
      await api.patch(`/installers/${inst.id}`, { ...inst, is_default: true });
      toast.success(`„${inst.name}" ist jetzt der Standard-Installation`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Installation" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><Wrench className="text-[#00E575]" /></div>
            <div>
              <h1 className="font-display text-2xl font-semibold">Installation</h1>
              <p className="text-sm text-zinc-300 font-medium">Installationsbetriebe verwalten – Standardpartner für die Projektübergabe pflegen</p>
            </div>
          </div>
          <button
            onClick={startNew}
            data-testid="installer-new-btn"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm hover:bg-[#00C964] active:scale-95 transition"
          >
            <Plus size={16} /> Neuer Installation
          </button>
        </div>

        {loading && <div className="text-zinc-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Lade…</div>}

        {!loading && items.length === 0 && !editing && (
          <div className="bg-[#18181B] border border-dashed border-zinc-700 rounded-2xl p-8 text-center text-zinc-500">
            Noch keine Installation angelegt. Klicke oben rechts auf „Neuer Installation".
          </div>
        )}

        {/* Editor */}
        {editing && (
          <div className="bg-[#18181B] border border-[#00E575]/40 rounded-2xl p-5 mb-6 space-y-3" data-testid="installer-editor">
            <h2 className="font-display text-lg font-semibold mb-1">
              {editing.id ? "Installation bearbeiten" : "Neuer Installation"}
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Firmenname *</label>
                <input data-testid="installer-name" className={inputCls} value={editing.name} onChange={(e) => updField("name", e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Ansprechpartner</label>
                <input data-testid="installer-contact" className={inputCls} value={editing.contact_person} onChange={(e) => updField("contact_person", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Straße</label>
                <input data-testid="installer-street" className={inputCls} value={editing.street} onChange={(e) => updField("street", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Hausnummer</label>
                <input data-testid="installer-house" className={inputCls} value={editing.house_number} onChange={(e) => updField("house_number", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>PLZ</label>
                <input data-testid="installer-zip" className={inputCls} value={editing.zip} onChange={(e) => updField("zip", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Stadt</label>
                <input data-testid="installer-city" className={inputCls} value={editing.city} onChange={(e) => updField("city", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>E-Mail</label>
                <input data-testid="installer-email" className={inputCls} value={editing.email} onChange={(e) => updField("email", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Telefon</label>
                <input data-testid="installer-phone" className={inputCls} value={editing.phone} onChange={(e) => updField("phone", e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Interne Notiz</label>
                <textarea data-testid="installer-notes" className={inputCls + " h-20 py-2"} value={editing.notes} onChange={(e) => updField("notes", e.target.value)} />
              </div>
              <label className="col-span-2 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!editing.is_default} onChange={(e) => updField("is_default", e.target.checked)} data-testid="installer-default-toggle" />
                <span className="text-sm text-zinc-200">Als <b>Standard-Installation</b> festlegen (wird automatisch vorausgewählt)</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm" data-testid="installer-cancel-btn">Abbrechen</button>
              <button onClick={save} disabled={savingId !== null} data-testid="installer-save-btn" className="h-10 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm hover:bg-[#00C964] disabled:opacity-50 inline-flex items-center gap-2">
                {savingId ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
              </button>
            </div>
          </div>
        )}

        {/* Liste */}
        <div className="space-y-3">
          {items.map((inst) => (
            <div key={inst.id} data-testid={`installer-row-${inst.id}`} className={`bg-[#18181B] border rounded-2xl p-4 ${inst.is_default ? "border-[#00E575]/50" : "border-zinc-800"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-zinc-100 truncate">{inst.name}</h3>
                    {inst.is_default && (
                      <span className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider bg-[#00E575]/20 text-[#00E575] border border-[#00E575]/40 rounded-md px-2 py-0.5">
                        <Star size={10} /> Standard
                      </span>
                    )}
                  </div>
                  {inst.contact_person && <div className="text-xs text-zinc-400 mt-0.5">z. Hd. {inst.contact_person}</div>}
                  <div className="text-xs text-zinc-300 mt-1">
                    {[inst.street, inst.house_number].filter(Boolean).join(" ")}{(inst.street || inst.house_number) && (inst.zip || inst.city) ? " · " : ""}{[inst.zip, inst.city].filter(Boolean).join(" ")}
                  </div>
                  <div className="text-xs text-zinc-300 mt-0.5">
                    {inst.email && <span>{inst.email}</span>}{inst.email && inst.phone && " · "}{inst.phone && <span>{inst.phone}</span>}
                  </div>
                  {inst.notes && <div className="text-xs text-zinc-400 mt-2 bg-zinc-900/50 rounded-lg px-2 py-1.5">{inst.notes}</div>}
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  {!inst.is_default && (
                    <button onClick={() => setDefault(inst)} data-testid={`installer-default-${inst.id}`} className="text-[12px] text-zinc-400 hover:text-amber-400 inline-flex items-center gap-1" title="Als Standard festlegen">
                      <StarOff size={11} /> als Standard
                    </button>
                  )}
                  <button onClick={() => startEdit(inst)} data-testid={`installer-edit-${inst.id}`} className="h-8 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs">Bearbeiten</button>
                  <button onClick={() => remove(inst)} data-testid={`installer-delete-${inst.id}`} className="h-8 px-3 rounded-lg bg-zinc-800/60 hover:bg-red-500/20 text-red-400 text-xs inline-flex items-center gap-1">
                    <Trash2 size={12} /> Löschen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
